'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { GoogleLogin } from '@react-oauth/google';
import { SplaceWordmark } from '@/components/ui/Logo';
import { useRouter } from 'next/navigation';
import { api, ApiError, useAuth, type AuthUser, toast } from '@splace/core';
const HAS_GOOGLE = !!process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;

type State = 'email' | 'otp' | 'success';

const COPY: Record<State, { eyebrow: string; title: string; sub: string }> = {
  email:   { eyebrow: 'WELCOME BACK',     title: 'Sign back in.',   sub: 'Enter your email — we\'ll send you a one-time code. No password needed.' },
  otp:     { eyebrow: 'CHECK YOUR INBOX', title: 'Enter the code.', sub: 'We sent a 6-digit code to your email. Drop it below to sign in.' },
  success: { eyebrow: 'SIGNED IN',        title: 'Welcome back.',   sub: 'Routing you back to the feed…' },
};

export default function LoginPage() {
  const [state, setState] = useState<State>('email');
  const [email, setEmail] = useState('');
  const router = useRouter();
  const setUser = useAuth(s => s.setUser);

  return (
    <div className="lg-root" data-theme="dark">
      <BrandPanel state={state} />
      <main className="lg-main">
        <header className="lg-header">
          <span className="lg-header-mute">New to Splace?</span>
          <Link href="/onboarding" className="lg-header-link">Create an account →</Link>
        </header>

        <div className="lg-stage">
          {state === 'email' && (
            <EmailState email={email} setEmail={setEmail} onSent={() => setState('otp')} />
          )}
          {state === 'otp' && (
            <OtpState email={email} onBack={() => setState('email')} onSuccess={(user) => {
              setUser(user);
              setState('success');
              setTimeout(() => router.push('/feed'), 1000);
            }} />
          )}
          {state === 'success' && <SuccessState />}
        </div>

        <footer className="lg-footer">
          <Link href="/help" className="lg-footer-link">Help</Link>
          <Link href="/privacy" className="lg-footer-link">Privacy</Link>
          <Link href="/terms" className="lg-footer-link">Terms</Link>
          <span className="sp-mono lg-footer-mute">© 2026 Splace</span>
        </footer>
      </main>
      <style>{CSS}</style>
    </div>
  );
}

function BrandPanel({ state }: { state: State }) {
  const copy = COPY[state];
  return (
    <aside className="lg-brand">
      <div className="lg-brand-top">
        <Link href="/"><SplaceWordmark height={32} style={{ color: 'var(--text)' }} /></Link>
      </div>
      <div className="lg-brand-mid">
        <div className="lg-brand-eyebrow sp-mono">{copy.eyebrow}</div>
        <h2 className="lg-brand-title sp-display">{copy.title}</h2>
        <p className="lg-brand-sub">{copy.sub}</p>
      </div>
      <div className="lg-brand-stat">
        <ul className="lg-bullets">
          <li>
            <span className="lg-bullet-dot" />
            <div>
              <div className="lg-bullet-title">Anonymous by default</div>
              <p>Spin up an avatar — name, photo, timer. No one sees the real you yet.</p>
            </div>
          </li>
          <li>
            <span className="lg-bullet-dot" />
            <div>
              <div className="lg-bullet-title">Chat while it lasts</div>
              <p>1 to 30 minutes per avatar. When the timer ends, the chats go with it.</p>
            </div>
          </li>
          <li>
            <span className="lg-bullet-dot" />
            <div>
              <div className="lg-bullet-title">Link to reveal</div>
              <p>Both tap link, both see each other&apos;s real profile. Otherwise it&apos;s gone.</p>
            </div>
          </li>
        </ul>
      </div>
      <div className="lg-brand-foot">
        <span className="sp-mono">SPLACE / LOGIN</span>
        <span className="sp-mono">v1.0</span>
      </div>
    </aside>
  );
}

function EmailState({ email, setEmail, onSent }: { email: string; setEmail: (e: string) => void; onSent: () => void }) {
  const [loading, setLoading] = useState(false);
  const setUser = useAuth(s => s.setUser);
  const router = useRouter();
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  async function go() {
    if (!valid || loading) return;
    setLoading(true);
    try {
      await api.auth.sendOtp(email);
      toast.info('Code sent — check your inbox');
      onSent();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to send code');
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleSuccess(idToken?: string) {
    if (!idToken) {
      toast.error('Google sign-in failed');
      return;
    }
    try {
      const result = await api.auth.google(idToken);
      setUser(result.user);
      if (result.isNewUser) {
        toast.success('Welcome — let’s finish your profile');
        router.push('/onboarding?from=google');
      } else {
        toast.success('Welcome back!');
        router.push('/feed');
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Google sign-in failed');
    }
  }

  return (
    <div className="lg-card">
      <h1 className="lg-form-title sp-display">Sign in to Splace</h1>
      <p className="lg-form-sub">Enter your email — we&apos;ll send you a one-time code. No password needed.</p>
      <div className="lg-form">
        <label className="lg-label">Email</label>
        <input className="sp-input lg-input" type="email" placeholder="you@example.com"
          value={email} onChange={e => setEmail(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && go()} autoFocus disabled={loading} />
        <button className="lg-btn lg-btn--primary" onClick={go} disabled={!valid || loading}>
          {loading ? 'Sending…' : 'Email me a code'}
        </button>
        {HAS_GOOGLE && (
          <>
            <div className="lg-divider"><span>or</span></div>
            <div className="lg-google-wrap">
              <GoogleLogin
                onSuccess={(cred) => handleGoogleSuccess(cred.credential)}
                onError={() => toast.error('Google sign-in failed')}
                theme="filled_black"
                text="continue_with"
                shape="pill"
              />
            </div>
          </>
        )}
        <p className="lg-fineprint">By signing in you agree to Splace&apos;s <Link href="/terms">Terms</Link> and <Link href="/privacy">Privacy Policy</Link>.</p>
      </div>
      <div className="lg-form-foot"><span className="lg-foot-link">New here? <Link href="/onboarding">Create an account</Link></span></div>
    </div>
  );
}

function OtpState({ email, onBack, onSuccess }: { email: string; onBack: () => void; onSuccess: (user: AuthUser) => void }) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const [err, setErr] = useState('');
  const [seconds, setSeconds] = useState(42);
  const [loading, setLoading] = useState(false);
  const router = useRouter();

  useEffect(() => { refs.current[0]?.focus(); }, []);
  useEffect(() => {
    if (seconds <= 0) return;
    const id = setTimeout(() => setSeconds(s => s - 1), 1000);
    return () => clearTimeout(id);
  }, [seconds]);

  function handleChange(i: number, v: string) {
    const ch = v.replace(/\D/g, '').slice(-1);
    const c = [...code]; c[i] = ch; setCode(c); setErr('');
    if (ch && i < 5) refs.current[i + 1]?.focus();
  }
  function handleKey(i: number, e: React.KeyboardEvent) {
    if (e.key === 'Backspace' && !code[i] && i > 0) refs.current[i - 1]?.focus();
  }
  function handlePaste(e: React.ClipboardEvent) {
    const txt = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!txt) return;
    e.preventDefault();
    const arr = txt.split('').concat(['', '', '', '', '', '']).slice(0, 6);
    setCode(arr);
    if (txt.length === 6) setTimeout(() => verify(txt), 350);
    else refs.current[txt.length]?.focus();
  }

  async function verify(codeStr?: string) {
    const c = codeStr ?? code.join('');
    if (c.length < 6 || loading) return;
    setLoading(true); setErr('');
    try {
      const result = await api.auth.verifyOtp(email, c);
      if (result.isNewUser) {
        toast.info('Email verified — complete your profile to finish signing up');
        router.push(`/onboarding?from=login&email=${encodeURIComponent(email)}`);
      } else {
        onSuccess(result.user);
      }
    } catch (err) {
      setErr(err instanceof ApiError ? err.message : 'Invalid code');
      setCode(['', '', '', '', '', '']);
      refs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  }

  async function resend() {
    try {
      await api.auth.sendOtp(email);
      setSeconds(42); setCode(['', '', '', '', '', '']); setErr('');
      refs.current[0]?.focus();
      toast.info('New code sent');
    } catch { toast.error('Failed to resend'); }
  }

  const filled = code.every(c => c !== '');

  return (
    <div className="lg-card">
      <h1 className="lg-form-title sp-display">Check your inbox</h1>
      <p className="lg-form-sub">We sent a 6-digit code to <strong>{email}</strong>. <button className="lg-link-btn" onClick={onBack}>Wrong email?</button></p>
      <div className="lg-form">
        <div className="lg-otp" onPaste={handlePaste}>
          {code.map((c, i) => (
            <input key={i} ref={el => { refs.current[i] = el; }}
              className={`lg-otp-cell ${err ? 'is-error' : ''}`} type="text" inputMode="numeric" maxLength={1}
              value={c} onChange={e => handleChange(i, e.target.value)} onKeyDown={e => handleKey(i, e)}
              disabled={loading} />
          ))}
        </div>
        {err && <div className="lg-error"><AlertIcon /> {err}</div>}
        <button className="lg-btn lg-btn--primary" onClick={() => verify()} disabled={!filled || loading}>
          {loading ? 'Verifying…' : 'Verify and sign in'}
        </button>
        <div className="lg-row-between" style={{ marginTop: 4 }}>
          <button className="lg-link-btn" onClick={onBack}>← Back</button>
          {seconds > 0
            ? <span className="lg-fineprint">Resend in <span className="sp-mono">0:{String(seconds).padStart(2, '0')}</span></span>
            : <button className="lg-link-btn" onClick={resend}>Resend code</button>}
        </div>
        <p className="lg-fineprint" style={{ textAlign: 'center' }}>Tip: you can paste the whole code at once.</p>
      </div>
    </div>
  );
}

function SuccessState() {
  return (
    <div className="lg-card" style={{ textAlign: 'center' }}>
      <div className="lg-success">
        <div className="lg-success-mark"><CheckIcon size={32} /></div>
        <p className="lg-form-sub" style={{ margin: 0 }}>Routing you to your feed…</p>
      </div>
    </div>
  );
}

function CheckIcon({ size = 14 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 16 16" fill="none"><path d="M3 8.5L6.5 12L13 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}
function AlertIcon() {
  return <svg width={14} height={14} viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.6" /><path d="M8 4.5v4M8 11v.01" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>;
}
function GoogleIcon() {
  return <svg width={16} height={16} viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.5-5.9 7.8-11.3 7.8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 5.7 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z" /><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16.1 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 7.7 29.3 6 24 6c-7.6 0-14.1 4.3-17.7 10.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35 26.7 36 24 36c-5.4 0-9.9-3.4-11.3-8L6 32.9C9.5 39.5 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2-2 3.7-3.6 5l6.2 5.2C42 35.4 44 30 44 24c0-1.2-.1-2.4-.4-3.5z" /></svg>;
}

const CSS = `
.lg-root { display:grid; grid-template-columns:1fr 1fr; height:100dvh; overflow-y:auto; background:var(--bg); }
.lg-google-wrap { display:flex; justify-content:center; width:100%; overflow:hidden; }
.lg-google-wrap > * { max-width:100%; }
.lg-brand { background:var(--bg-elev); border-right:1px solid var(--line); padding:48px 56px; display:flex; flex-direction:column; position:relative; overflow:hidden; }
.lg-brand::before { content:""; position:absolute; top:-200px; right:-200px; width:500px; height:500px; background:radial-gradient(circle,color-mix(in oklab,var(--accent) 25%,transparent) 0%,transparent 60%); pointer-events:none; filter:blur(20px); }
.lg-brand::after { content:""; position:absolute; bottom:-150px; left:-150px; width:400px; height:400px; background:radial-gradient(circle,oklch(40% 0.20 320 / 0.4) 0%,transparent 60%); pointer-events:none; filter:blur(20px); }
.lg-brand > * { position:relative; z-index:1; }
.lg-brand-top { margin-bottom:56px; }
.lg-wordmark { height:32px; width:auto; }
.lg-brand-mid { flex:1; display:flex; flex-direction:column; justify-content:center; max-width:480px; }
.lg-brand-eyebrow { font-size:11px; letter-spacing:0.14em; color:var(--text-dim); font-weight:500; margin-bottom:20px; text-transform:uppercase; }
.lg-brand-title { font-size:64px; line-height:1.0; margin:0 0 20px; letter-spacing:-0.03em; }
.lg-brand-sub { font-size:17px; line-height:1.5; color:var(--text-muted); margin:0; }
.lg-brand-stat { margin:56px 0 32px; max-width:440px; }
.lg-bullets { list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:18px; }
.lg-bullets li { display:flex; gap:14px; align-items:flex-start; }
.lg-bullet-dot { width:8px; height:8px; border-radius:50%; background:var(--accent); margin-top:8px; flex-shrink:0; box-shadow:0 0 0 4px color-mix(in oklab,var(--accent) 18%,transparent); }
.lg-bullet-title { font-size:14px; font-weight:600; color:var(--text); margin-bottom:2px; }
.lg-bullets p { font-size:13px; line-height:1.5; color:var(--text-muted); margin:0; }
.lg-brand-foot { display:flex; justify-content:space-between; font-size:11px; color:var(--text-dim); letter-spacing:0.06em; padding-top:24px; border-top:1px solid var(--line); }
.lg-main { display:flex; flex-direction:column; background:var(--bg); }
.lg-header { display:flex; justify-content:flex-end; align-items:center; gap:12px; padding:32px 56px; font-size:13px; }
.lg-header-mute { color:var(--text-muted); }
.lg-header-link { color:var(--text); font-weight:600; text-decoration:none; padding:8px 14px; border-radius:var(--r-pill); border:1px solid var(--line); transition:all .15s; }
.lg-header-link:hover { border-color:var(--text-muted); }
.lg-stage { flex:1; display:flex; align-items:center; justify-content:center; padding:24px 56px; }
.lg-footer { display:flex; gap:24px; padding:24px 56px; font-size:12px; border-top:1px solid var(--line); align-items:center; }
.lg-footer-link { color:var(--text-muted); text-decoration:none; }
.lg-footer-link:hover { color:var(--text); }
.lg-footer-mute { margin-left:auto; color:var(--text-dim); }
.lg-card { width:100%; max-width:440px; }
.lg-form-title { font-size:36px; line-height:1.05; margin:0 0 12px; letter-spacing:-0.02em; }
.lg-form-sub { font-size:15px; color:var(--text-muted); margin:0 0 28px; line-height:1.5; }
.lg-form { display:flex; flex-direction:column; gap:12px; }
.lg-form-foot { margin-top:20px; text-align:center; font-size:13px; color:var(--text-muted); }
.lg-foot-link a { color:var(--text); font-weight:600; text-decoration:none; }
.lg-label { font-size:12px; text-transform:uppercase; letter-spacing:0.08em; font-weight:600; color:var(--text-muted); margin-bottom:-6px; margin-top:4px; }
.lg-input { font-size:15px; padding:14px 16px; }
.lg-btn { display:inline-flex; align-items:center; justify-content:center; gap:8px; padding:14px 24px; border-radius:var(--r-pill); font:inherit; font-weight:600; font-size:14px; border:1px solid transparent; cursor:pointer; transition:all .15s; width:100%; }
.lg-btn--primary { background:var(--accent); color:var(--accent-fg); border-color:var(--accent); }
.lg-btn--primary:hover:not(:disabled) { filter:brightness(1.08); transform:translateY(-1px); }
.lg-btn--primary:disabled { opacity:.35; cursor:not-allowed; }
.lg-btn--social { background:var(--bg-elev); border-color:var(--line); color:var(--text); padding:12px 18px; justify-content:center; }
.lg-btn--social:hover { border-color:var(--text-muted); }
.lg-link-btn { background:none; border:none; padding:0; color:var(--accent); font-weight:600; cursor:pointer; font-size:inherit; font-family:inherit; }
.lg-link-btn:hover { text-decoration:underline; }
.lg-divider { display:flex; align-items:center; gap:12px; color:var(--text-dim); font-size:12px; margin:8px 0; }
.lg-divider::before,.lg-divider::after { content:""; flex:1; height:1px; background:var(--line); }
.lg-row { display:flex; gap:12px; align-items:center; }
.lg-row-between { display:flex; justify-content:space-between; align-items:center; font-size:13px; }
.lg-fineprint { font-size:12px; color:var(--text-dim); line-height:1.5; }
.lg-fineprint a { color:var(--text-muted); }
.lg-error { display:flex; align-items:center; gap:8px; background:oklch(70% 0.20 25 / 0.1); border:1px solid oklch(70% 0.20 25 / 0.3); color:oklch(80% 0.20 25); font-size:13px; padding:10px 12px; border-radius:var(--r-sm); }
.lg-otp { display:flex; gap:clamp(6px,2vw,10px); margin:4px 0; justify-content:center; }
.lg-otp-cell { flex:1; min-width:0; max-width:56px; aspect-ratio:50/60; border-radius:var(--r-sm); border:1px solid var(--line); background:var(--bg-input); color:var(--text); font-size:22px; font-weight:600; font-family:var(--font-mono); text-align:center; outline:none; padding:0; }
.lg-otp-cell:focus { border-color:var(--accent); box-shadow:0 0 0 3px color-mix(in oklab,var(--accent) 25%,transparent); }
.lg-otp-cell.is-error { border-color:oklch(70% 0.20 25); }
.lg-success { display:flex; flex-direction:column; align-items:center; gap:16px; padding:40px 0; }
.lg-success-mark { width:80px; height:80px; border-radius:50%; background:oklch(82% 0.18 145); color:#0E1116; display:flex; align-items:center; justify-content:center; position:relative; }
.lg-success-mark::before { content:""; position:absolute; inset:-8px; border-radius:50%; border:2px solid oklch(82% 0.18 145); opacity:0.4; animation:lg-ring 2s ease-out infinite; }
@keyframes lg-ring { 0% { transform:scale(0.95); opacity:0.6; } 100% { transform:scale(1.25); opacity:0; } }
@media (max-width:1100px) { .lg-brand { padding:36px 40px; } .lg-brand-title { font-size:48px; } .lg-brand-stat { display:none; } .lg-stage,.lg-header,.lg-footer { padding-left:40px; padding-right:40px; } }
@media (max-width:840px) { .lg-root { display:flex; flex-direction:column; height:auto; min-height:100dvh; overflow-y:visible; } .lg-brand { border-right:none; border-bottom:1px solid var(--line); padding:28px 24px; } .lg-brand-top { margin-bottom:24px; } .lg-brand-mid { max-width:none; } .lg-brand-title { font-size:32px; } .lg-brand-sub { font-size:15px; } .lg-brand-foot { display:none; } .lg-main { flex:1; } .lg-header { padding:20px 24px; flex-wrap:wrap; justify-content:flex-end; } .lg-stage { padding:24px; } .lg-footer { padding:20px 24px; flex-wrap:wrap; gap:16px; } .lg-footer-mute { margin-left:0; flex-basis:100%; } .lg-form-title { font-size:28px; } }
@media (max-width:420px) { .lg-brand { padding:24px 20px; } .lg-header { padding:16px 20px; font-size:12px; } .lg-stage { padding:20px; } .lg-footer { padding:16px 20px; gap:12px; } .lg-otp-cell { font-size:20px; } }
`;
